/**
 * Phase 5 — BS whitelist WP multi-year tabs + Drawer period locks.
 * @see docs/multi-year-phases/multi-year-phase5-bs-wp-drawer.md
 */
import type {AuditPeriodSlice, AuditSpanType} from '@/utils/auditPeriodSplit';

/** MULTI 下从 BS 打开时，WP 内启用 Period Tabs 的 code */
export const BS_MULTI_YEAR_WP_CODES = [
    'BS-NCA-PPE',
    'BS-NCA-IA',
    'BS-NCA-ITL',
    'BS-NCA-ISUB',
    'BS-NCA-IASS',
    'BS-CL-BLS',
    'BS-CL-OFL',
    'BS-NCL-BLS',
    'BS-NCL-OFL',
    'BS-CL-TAXP',
    'BS-CA-TAXR',
] as const;

export type BsMultiYearWpCode = (typeof BS_MULTI_YEAR_WP_CODES)[number];

export function usesBsMultiYearWpTabs(code: string, spanType: AuditSpanType): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return spanType === 'MULTI'
        && (BS_MULTI_YEAR_WP_CODES as readonly string[]).includes(upper);
}

/**
 * P7 §2.4 — paper-registry WP get/save routing under MULTI.
 * - pl-slice: legacy single-year shape + yearOfAssessment (kept for bridge typing; PL MULTI no longer uses it)
 * - bs-multi: BS §5.6.2 whitelist + all PL-* — multi-year get/save (batch + YOA Map)
 * - single: existing single-year path (#20 / non-whitelist BS)
 */
export type WpDataMode = 'single' | 'pl-slice' | 'bs-multi';

export function resolveWpDataMode(
    code: string,
    spanType: AuditSpanType | string | null | undefined,
): WpDataMode {
    const span = String(spanType || 'SINGLE').toUpperCase() === 'MULTI' ? 'MULTI' : 'SINGLE';
    if (span !== 'MULTI') return 'single';

    const upper = String(code || '').trim().toUpperCase();
    // All ProfitLoss paper-registry codes: same wire as REN / PPE —
    // GET yoaMap + Save full `{ [yoa]: { details }, issueMark? }` batch
    // (Multiple outer Tabs + Total inner Tabs share this path).
    if (upper.startsWith('PL')) return 'bs-multi';
    if (usesBsMultiYearWpTabs(upper, 'MULTI')) return 'bs-multi';
    if (upper.startsWith('BS')) return 'single';
    return 'single';
}

export function wpDataModeNeedsYoa(mode: WpDataMode): boolean {
    return mode === 'pl-slice' || mode === 'bs-multi';
}

export function wpDataModeUsesBatchSave(mode: WpDataMode): boolean {
    return mode === 'bs-multi';
}

export type WpPeriodTabMode = 'multi' | 'single';

/** PL codes that need WP-inner Period Tabs under MULTI (tax-affecting per YOA). */
export const PL_MULTI_YEAR_WP_CODES = [
    'PL-OI-II', // QXI1 Interest Income — bank = deduct in tax com, per YOA
    'PL-OI-SFG', // Subsidies from government — same WP as QXI1, per YOA
] as const;

export function usesPlMultiYearWpTabs(code: string, spanType: AuditSpanType): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return spanType === 'MULTI'
        && (PL_MULTI_YEAR_WP_CODES as readonly string[]).includes(upper);
}

export function resolveWpPeriodTabMode(args: {
    code: string;
    statement: 'BS' | 'PL';
    spanType: AuditSpanType;
}): WpPeriodTabMode {
    const code = String(args.code || '').trim().toUpperCase();
    if (args.spanType !== 'MULTI') return 'single';

    // PL-TAX：跟 PL Period Tab，不做 WP 内多 Tab
    if (code === 'PL-TAX' && args.statement === 'PL') return 'single';

    // QXI1 Interest Income：每年银行利息是否 deduct in tax com，需 WP 内选年
    if (args.statement === 'PL' && usesPlMultiYearWpTabs(code, 'MULTI')) return 'multi';

    // BS Tax + 其余白名单：WP 内多 Tab
    if (args.statement === 'BS' && usesBsMultiYearWpTabs(code, 'MULTI')) return 'multi';

    return 'single';
}

export type AdjPeriodLockInitiator = {
    statement: 'BS' | 'PL';
    code: string;
    /** BS 多 Tab / PL：发起时已有；BS 非多 Tab 首次前可为 null */
    sliceKey: string | null;
};

export type AdjPeriodLockContext = {
    spanType: AuditSpanType;
    initiator: AdjPeriodLockInitiator;
    /** 本笔已锁定的锚点（BS 非多 Tab 首次选中后写入） */
    anchorSliceKey: string | null;
    /** 是否 append 再次打开 Drawer */
    isAppend: boolean;
    periods: AuditPeriodSlice[];
};

export type AdjTargetKind = 'PL' | 'BS_MULTI' | 'BS_NON_MULTI';

export function classifyAdjTargetKind(code: string, statement?: 'BS' | 'PL' | null): AdjTargetKind {
    const upper = String(code || '').trim().toUpperCase();
    const stmt = statement
        || (upper.startsWith('PL-') ? 'PL' : 'BS');
    if (stmt === 'PL' || upper.startsWith('PL-')) return 'PL';
    if (usesBsMultiYearWpTabs(upper, 'MULTI')) return 'BS_MULTI';
    return 'BS_NON_MULTI';
}

/** Initiator is BS non-whitelist (no WP Period Tabs). */
export function isBsNonMultiInitiator(initiator: AdjPeriodLockInitiator): boolean {
    return initiator.statement === 'BS'
        && !usesBsMultiYearWpTabs(initiator.code, 'MULTI');
}

/**
 * Whether FsSelectionDrawer should render selectable YOA tabs.
 * Only BS non-multi initiator before an anchor — otherwise hide (use initiator YOA silently).
 * @see docs/multi-year/fsadjustment-multi-adj-yoa-column-gap.md §4.1
 */
export function shouldShowDrawerYoaSelector(ctx: AdjPeriodLockContext): boolean {
    if (ctx.spanType !== 'MULTI') return false;
    if (!ctx.periods || ctx.periods.length < 2) return false;
    if (!isBsNonMultiInitiator(ctx.initiator)) return false;
    if (ctx.initiator.sliceKey) return false;
    if (ctx.anchorSliceKey) return false;
    return true;
}

/**
 * Whether confirming a target requires an explicit Drawer YOA (user pick or silent initiator/anchor).
 * BS non-multi → BS non-multi: false.
 * BS non-multi → PL / BS multi: true (must pick).
 * BS multi / PL initiator → PL / BS multi: true (but YOA comes from initiator, not UI).
 */
export function shouldRequireDrawerYoa(
    ctx: AdjPeriodLockContext,
    targetKind: AdjTargetKind,
): boolean {
    if (ctx.spanType !== 'MULTI') return false;
    if (targetKind === 'BS_NON_MULTI') return false;
    return targetKind === 'PL' || targetKind === 'BS_MULTI';
}

/**
 * Whether confirm payload should stamp sliceKey / yearOfAssessment from Drawer/initiator.
 * Never for BS non-multi targets (1.1 / 2.1 / 3.1).
 */
export function shouldAttachDrawerYoaPayload(
    ctx: AdjPeriodLockContext,
    targetKind: AdjTargetKind,
): boolean {
    if (ctx.spanType !== 'MULTI') return false;
    return shouldRequireDrawerYoa(ctx, targetKind);
}

/** Effective slice key for Drawer confirm (initiator / anchor / user pick). */
export function resolveDrawerEffectiveSliceKey(
    ctx: AdjPeriodLockContext,
    drawerSliceKey: string | null | undefined,
): string {
    const fromInitiator = (ctx.initiator.sliceKey || '').trim();
    if (fromInitiator) return fromInitiator;
    const fromAnchor = (ctx.anchorSliceKey || '').trim();
    if (fromAnchor) return fromAnchor;
    return String(drawerSliceKey || '').trim();
}

/**
 * Resolve YOA for toPaper WP assemble under MULTI (Drawer pick / initiator / anchor).
 * Returns null when target is not pl-slice / bs-multi.
 */
export function resolveAdjAssembleYearOfAssessment(args: {
    targetCode: string;
    spanType?: AuditSpanType | string | null;
    bridgeYoa?: string | null;
    periodLock?: {
        spanType?: AuditSpanType | string | null;
        anchorSliceKey?: string | null;
        initiator?: {sliceKey?: string | null} | null;
        periods?: Array<{key: string; yearOfAssessment?: string | null}> | null;
    } | null;
}): string | null {
    const spanRaw = args.periodLock?.spanType || args.spanType || 'SINGLE';
    const span: AuditSpanType = String(spanRaw).toUpperCase() === 'MULTI' ? 'MULTI' : 'SINGLE';
    const mode = resolveWpDataMode(args.targetCode, span);
    if (!wpDataModeNeedsYoa(mode)) return null;

    const bridge = String(args.bridgeYoa || '').trim();
    if (bridge) return bridge;

    const lock = args.periodLock;
    if (!lock?.periods?.length) return null;
    const sliceKey = String(
        lock.anchorSliceKey || lock.initiator?.sliceKey || '',
    ).trim();
    if (!sliceKey) return null;
    const hit = lock.periods.find((p) => p.key === sliceKey);
    const yoa = String(hit?.yearOfAssessment || '').trim();
    return yoa || null;
}

/**
 * §5.7 — allowed slice keys for Drawer target selection.
 * Returns `'ANY'` or a list of allowed keys (empty = none selectable yet).
 */
export function resolveAllowedSliceKeys(
    ctx: AdjPeriodLockContext,
    targetKind: AdjTargetKind,
    allSliceKeys: string[],
): string[] | 'ANY' {
    if (ctx.spanType !== 'MULTI') return 'ANY';

    const initiatorIsBsMulti =
        ctx.initiator.statement === 'BS'
        && usesBsMultiYearWpTabs(ctx.initiator.code, 'MULTI');
    const initiatorIsPl = ctx.initiator.statement === 'PL';
    const initiatorIsBsNonMulti =
        ctx.initiator.statement === 'BS' && !initiatorIsBsMulti;

    // Target BS non-multi → no YOA restriction (and no YOA payload)
    if (targetKind === 'BS_NON_MULTI') return 'ANY';

    const anchor = ctx.anchorSliceKey ?? ctx.initiator.sliceKey;

    // BS multi / PL initiator → only initiator/anchor slice
    if (initiatorIsBsMulti || initiatorIsPl) {
        return anchor ? [anchor] : [];
    }

    // BS non-multi initiator:
    // - first open (no anchor): ANY key may be picked for PL / BS multi (UI requires explicit pick)
    // - after anchor / append: only anchor
    if (initiatorIsBsNonMulti) {
        if (ctx.anchorSliceKey) return [ctx.anchorSliceKey];
        if (!ctx.isAppend && ctx.anchorSliceKey == null) {
            return 'ANY';
        }
        return anchor ? [anchor] : [];
    }

    // Target PL / BS multi → only anchor
    if (targetKind === 'PL' || targetKind === 'BS_MULTI') {
        return anchor ? [anchor] : [];
    }

    return 'ANY';
}

export function sliceToPeriodScope(slice: AuditPeriodSlice, options?: {isFirstSlice?: boolean}) {
    return {
        periodFrom: slice.periodFrom,
        periodTo: slice.periodTo,
        yearOfAssessment: slice.yearOfAssessment,
        sliceKey: slice.key,
        isFirstSlice: options?.isFirstSlice ?? slice.index === 0,
    };
}

/**
 * WP PerCheck last-YOA column visibility (aligns BankLoan `hideLastYoa`).
 * - Not FirstAudit → always show yoa + lastYoa
 * - FirstAudit + non-MULTI → hide lastYoa (yoa only)
 * - FirstAudit + MULTI → hide lastYoa only on first Period Tab (`isFirstSlice`)
 */
export function resolveWpIgnoreLastYear(
    isFirstAudit: boolean,
    periodScope?: {sliceKey?: string; isFirstSlice?: boolean} | null,
): boolean {
    if (!isFirstAudit) return false;
    const isMulti = Boolean(periodScope?.sliceKey);
    if (!isMulti) return true;
    return periodScope?.isFirstSlice === true;
}
